import type { BillType } from "@/data/model/Enums";
import type { IBaseOpenBill } from "@/data/model/Operator";

import type { IOrderItemComment } from "./Order";
import type { IMultiLanguageText } from "./Shared";

export interface IBill {
    id: string;
    tableId: string;
    opened: Date;
    closed?: Date;
    total: number;
    totalPaid: number;
    unpaid: number;
    items: Array<IBillItem>;
    paidItems: Array<IBillItem>;
    selections: Array<IUserItemSelection>;
    appliedDiscounts: Array<IBillDiscount>;
    type: BillType;
    customName?: string;
    discountAmount: number;
    manualEndUsersCount?: number;
}

export interface IBillDiscount {
    id: string;
    identityId: string;
    voucherId: string | undefined;
    discountId: string | undefined;
    createdDateTime: Date;
    amount: number;
}

export interface IBillItem {
    id: string;
    orderId: string;
    name?: IMultiLanguageText;
    internalName?: string;
    comment: IOrderItemComment | undefined;
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unpaidAmount: number;
    price: number;
    priceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;
}

export interface IBillItemOptionGroup {
    id: string;
    displayName: boolean;
    name?: IMultiLanguageText;
    internalName?: string;
    items: Array<IBillItemOption>;
}

export interface IBillItemOption {
    id: string;
    name?: IMultiLanguageText;
    internalName?: string;
    quantity: number;
    price: number;
    options: Array<IBillItemOptionGroup>;
}

export interface IUserItemSelection {
    identityId: string;
    quantity: number;
    lineItemId: string;
}

export interface ISelectedItem {
    quantity: number;
    lineItemId: string;
}

export class Bill {
    static getUnpaidLineItems(bill: IBill, identityId: string): UnpaidLineItem[] {
        const unpaidLineItems = bill.items.map(
            (lineItem) => new UnpaidLineItem(lineItem, Bill.getSelectedItemQuantity(identityId, lineItem, bill))
        );
        unpaidLineItems.sort((a, b) => a.sortOrder - b.sortOrder);

        return unpaidLineItems;
    }

    static getPaidLineItems(bill: IBill): PaidLineItem[] {
        const paidLineItems = bill.paidItems.map((lineItem) => new PaidLineItem(lineItem));
        paidLineItems.sort((a, b) => a.sortOrder - b.sortOrder);

        return paidLineItems;
    }

    static getSelectedItemQuantity(identityId: string, item: IBillItem, bill: IBill): number {
        // get all user selections for bill
        const lineSelections = bill.selections.filter((x) => x.identityId === identityId && x.lineItemId == item.id);

        // get all selected quantities for line item
        let quantity = 0;
        for (const selection of lineSelections) {
            quantity += selection.quantity;
        }
        return quantity;
    }

    static getUnpaidMaxDisplayedDigits(items: IBillItem[]) {
        let maxLength = 1;
        for (const item of items) {
            const itemDigits = item.unpaidQuantity.toString().length;
            maxLength = Math.max(maxLength, itemDigits);
        }
        return maxLength;
    }

    static getPaidMaxDisplayedDigits(items: IBillItem[]) {
        let maxLength = 1;
        for (const item of items) {
            const itemDigits = item.paidQuantity.toString().length;
            maxLength = Math.max(maxLength, itemDigits);
        }
        return maxLength;
    }
}

export class UnpaidLineItem {
    id: string;
    orderId: string;
    name?: IMultiLanguageText;
    internalName?: string;
    comment: IOrderItemComment | undefined;
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unpaidAmount: number;
    price: number;
    priceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;
    selectedQuantity: number;

    constructor(lineItem: IBillItem, selectedQuantity: number) {
        this.id = lineItem.id;
        this.orderId = lineItem.orderId;
        this.name = lineItem.name;
        this.internalName = lineItem.internalName;
        this.comment = lineItem.comment;
        this.quantity = lineItem.quantity;
        this.paidQuantity = lineItem.paidQuantity;
        this.unpaidQuantity = lineItem.unpaidQuantity;
        this.paidAmount = lineItem.paidAmount;
        this.unpaidAmount = lineItem.unpaidAmount;
        this.price = lineItem.price;
        this.priceWithOptions = lineItem.priceWithOptions;
        this.isZeroPriceItem = lineItem.isZeroPriceItem;
        this.sortOrder = lineItem.sortOrder;
        this.options = lineItem.options;
        this.selectedQuantity = selectedQuantity;
    }
}

export class PaidLineItem {
    id: string;
    orderId: string;
    name?: IMultiLanguageText;
    internalName?: string;
    comment: IOrderItemComment | undefined;
    quantity: number;
    paidQuantity: number;
    unpaidQuantity: number;
    paidAmount: number;
    unpaidAmount: number;
    price: number;
    priceWithOptions: number;
    isZeroPriceItem: boolean;
    sortOrder: number;
    options: Array<IBillItemOptionGroup>;

    constructor(lineItem: IBillItem) {
        this.id = lineItem.id;
        this.orderId = lineItem.orderId;
        this.name = lineItem.name;
        this.internalName = lineItem.internalName;
        this.comment = lineItem.comment;
        this.quantity = lineItem.quantity;
        this.paidQuantity = lineItem.paidQuantity;
        this.unpaidQuantity = lineItem.unpaidQuantity;
        this.paidAmount = lineItem.paidAmount;
        this.unpaidAmount = lineItem.unpaidAmount;
        this.price = lineItem.price;
        this.priceWithOptions = lineItem.priceWithOptions;
        this.isZeroPriceItem = lineItem.isZeroPriceItem;
        this.sortOrder = lineItem.sortOrder;
        this.options = lineItem.options;
    }
}

export type OpenBillSelectedEventData = {
    locationId: string;
    tableId: string;
    roomId: string;
    tableNumber: number;
    selectedOpenBill: IBaseOpenBill;
};

export type OpenBillRoomSelectedEventData = {
    locationId: string;
    roomId: string;
};

export type OpenBillTableData = {
    name: string;
    locationId: string;
    roomId: string;
    roomName: string;
    tableId: string;
    tableNumber: number;
    tableName: string;
    operatorName: string;
    occupancy: string;
    since: string;
    open: string;
    isDisabled: boolean;
    bill: IBaseOpenBill;
};
